import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
	Plus,
	MoreVertical,
	MessageSquare,
	Megaphone,
	Trash2,
	Pencil,
	CalendarDays,
	Loader2,
	Lock,
	KanbanSquare,
} from "lucide-react";

import MainContainer from "../../components/MainContainer";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import {
  Avatar,
  AvatarImage,
  AvatarFallback,
  AvatarPreview,
} from "../../components/ui/avatar";
import {
	DropdownMenu,
	DropdownMenuTrigger,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuSeparator,
	DropdownMenuSub,
	DropdownMenuSubTrigger,
	DropdownMenuSubContent,
	DropdownMenuPortal,
} from "../../components/ui/dropdown-menu";

import api from "../../services/api";
import openSocket from "../../services/socket-io";
import toastError from "../../errors/toastError";
import { cn } from "../../lib/utils";

import TicketEmAtendimentoModal from "../../components/TicketEmAtendimentoModal";
import { abrirConversaComContato } from "../../utils/abrirConversa";
import ColunaModal from "./ColunaModal";
import CartaoModal from "./CartaoModal";
import EmptyStateCard from "../../components/EmptyStateCard";

// O quadro: uma coluna por estado do trabalho, um cartão por cliente.
//
// A regra que sustenta a tela é a dos dois sentidos. Arrastar o cartão para
// "Em produção" avisa o cliente; mandar a resposta pronta no atendimento traz
// o cartão para cá. Quem trabalha no chat e quem trabalha no quadro veem a
// mesma verdade sem precisar contar um para o outro.

const formatarMoeda = (valor) =>
	valor === null || valor === undefined || valor === ""
		? null
		: Number(valor).toLocaleString("pt-BR", {
				style: "currency",
				currency: "BRL",
			});

const formatarData = (iso) => {
	if (!iso) return null;
	// Data pura (YYYY-MM-DD) montada por partes: `new Date("2026-08-13")` é
	// meia-noite UTC e, em fuso negativo, mostra o dia anterior.
	const [ano, mes, dia] = String(iso).slice(0, 10).split("-").map(Number);
	return new Date(ano, mes - 1, dia).toLocaleDateString("pt-BR", {
		day: "2-digit",
		month: "2-digit",
	});
};

const iniciais = (nome) =>
	(nome || "?")
		.trim()
		.split(/\s+/)
		.slice(0, 2)
		.map((p) => p[0]?.toUpperCase())
		.join("");

// A coluna "Contatos" vem do servidor com este id em texto — ela não existe no
// banco. É por isso que não se edita nem se apaga: o que ela mostra são os
// atendimentos em aberto agora, e some sozinha da lista quando o cliente vira
// cartão numa etapa.
const ID_CONTATOS = "contatos";

const Kanban = () => {
	const navigate = useNavigate();

	const [colunas, setColunas] = useState([]);
	// Cartão de um contato que já está com outro atendente.
	const [emAtendimento, setEmAtendimento] = useState(null);
	const [carregando, setCarregando] = useState(true);
	const [colunaEmEdicao, setColunaEmEdicao] = useState(null);
	const [cartaoEmEdicao, setCartaoEmEdicao] = useState(null);
	// Cartão sendo arrastado e coluna sob o cursor, para destacar o alvo.
	const arrastando = useRef(null);
	const [alvo, setAlvo] = useState(null);

	const carregar = useCallback(async () => {
		try {
			const { data } = await api.get("/kanban");
			setColunas(data);
		} catch (err) {
			toastError(err);
		}
		setCarregando(false);
	}, []);

	useEffect(() => {
		carregar();
	}, [carregar]);

	useEffect(() => {
		const socket = openSocket();

		const onKanban = () => carregar();
		const onTicket = () => carregar();

		socket.on("kanban", onKanban);
		socket.on("ticket", onTicket);

		return () => {
			try { socket.off("kanban", onKanban); } catch (_) {}
			try { socket.off("ticket", onTicket); } catch (_) {}
		};
	}, [carregar]);

	// Cartão de contato (atendimento em aberto) largado numa etapa vira cartão
	// de verdade: é o gesto que diz "este atendimento virou trabalho".
	const puxarParaOQuadro = async (cartao, columnId) => {
		try {
			await api.post("/kanban/cards", {
				columnId,
				contactId: cartao.contactId,
				title: cartao.title,
			});
			toast.success(`${cartao.title} entrou no quadro.`);
			carregar();
		} catch (err) {
			toastError(err);
		}
	};

	const moverCartao = async (cartao, columnId, position) => {
		if (columnId === ID_CONTATOS) {
			toast.info(
				"A coluna Contatos é automática: ela mostra os atendimentos em aberto. Para tirar alguém do quadro, exclua o cartão.",
			);
			return;
		}

		if (cartao.isTicket) {
			puxarParaOQuadro(cartao, columnId);
			return;
		}

		if (cartao.columnId === columnId && position === undefined) return;

		// Move na tela antes de a rede responder: arrastar tem de ser
		// instantâneo. O socket devolve a verdade do servidor logo em seguida.
		setColunas((atuais) =>
			atuais.map((coluna) => {
				if (coluna.id === cartao.columnId) {
					return {
						...coluna,
						cards: (coluna.cards || []).filter((c) => c.id !== cartao.id),
					};
				}
				if (coluna.id === columnId) {
					const cards = [...(coluna.cards || [])];
					const indice = position === undefined ? cards.length : position;
					cards.splice(indice, 0, { ...cartao, columnId });
					return { ...coluna, cards };
				}
				return coluna;
			}),
		);

		try {
			await api.post(`/kanban/cards/${cartao.id}/move`, {
				columnId,
				position,
			});
		} catch (err) {
			toastError(err);
			carregar();
		}
	};

	const excluirCartao = async (cartao) => {
		if (!window.confirm(`Excluir o cartão "${cartao.title}"?`)) return;

		try {
			await api.delete(`/kanban/cards/${cartao.id}`);
			toast.success("Cartão excluído.");
			carregar();
		} catch (err) {
			toastError(err);
		}
	};

	const excluirColuna = async (coluna) => {
		const cartoes = coluna.cards?.length || 0;

		if (cartoes > 0) {
			const destinos = colunas.filter((c) => c.id !== coluna.id);

			if (!destinos.length) {
				toast.error(
					"Esta é a única coluna e ela tem cartões. Crie outra antes de excluí-la.",
				);
				return;
			}

			const nomes = destinos.map((c, i) => `${i + 1} - ${c.name}`).join("\n");
			const escolha = window.prompt(
				`"${coluna.name}" tem ${cartoes} cartão(ões). Para onde eles vão?\n\n${nomes}\n\nDigite o número:`,
			);

			const destino = destinos[Number(escolha) - 1];
			if (!destino) return;

			try {
				await api.delete(
					`/kanban/columns/${coluna.id}?moverPara=${destino.id}`,
				);
				toast.success(`Coluna excluída. Os cartões foram para ${destino.name}.`);
				carregar();
			} catch (err) {
				toastError(err);
			}
			return;
		}

		if (!window.confirm(`Excluir a coluna "${coluna.name}"?`)) return;

		try {
			await api.delete(`/kanban/columns/${coluna.id}`);
			toast.success("Coluna excluída.");
			carregar();
		} catch (err) {
			toastError(err);
		}
	};

	const abrirConversa = async (cartao) => {
		if (!cartao.contactId) return;

		try {
			// O cartão leva para a conversa daquele cliente: é lá que o trabalho
			// continua, e reabrir pelo contato evita procurar na lista.
			const { ticket, emAtendimento: ocupado } = await abrirConversaComContato(
				{ contactId: cartao.contactId },
			);

			if (ocupado) {
				setEmAtendimento(ocupado);
				return;
			}

			navigate(`/tickets/${ticket.id}`);
		} catch (err) {
			toastError(err);
		}
	};

	const colunasReais = colunas.filter((c) => !c.isSystem);
	const total = colunasReais.reduce(
		(soma, c) => soma + (c.cards?.length || 0),
		0,
	);
	const emAberto =
		colunas.find((c) => c.isSystem)?.cards?.length || 0;

	return (
		<MainContainer>
			<TicketEmAtendimentoModal
				aberto={Boolean(emAtendimento)}
				atendimento={emAtendimento}
				aoFechar={() => setEmAtendimento(null)}
			/>

			{/* Cabeçalho: o nome do quadro e o estado dele em números. Quem chega
			    aqui quer saber quanta coisa está aberta antes de qualquer botão. */}
			<div className="flex shrink-0 flex-wrap items-end justify-between gap-4 border-b bg-background/80 px-6 py-4 backdrop-blur">
				<div className="min-w-0">
					<h1 className="text-[1.35rem] font-semibold tracking-tight">Kanban</h1>
					{!carregando && (
						<p className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted-foreground">
							<span className="tabular-nums">
								<span className="font-medium text-foreground">{emAberto}</span>{" "}
								em atendimento
							</span>
							<span className="text-border">·</span>
							<span className="tabular-nums">
								<span className="font-medium text-foreground">{total}</span>{" "}
								no quadro
							</span>
							<span className="text-border">·</span>
							<span className="tabular-nums">
								{colunasReais.length} etapa{colunasReais.length === 1 ? "" : "s"}
							</span>
						</p>
					)}
				</div>
				<div className="flex items-center gap-2">
					<Button
						variant="ghost"
						size="sm"
						onClick={() => setColunaEmEdicao({})}
						disabled={carregando}
					>
						<Plus className="h-4 w-4" />
						Nova etapa
					</Button>
					<Button
						size="sm"
						onClick={() => setCartaoEmEdicao({ columnId: colunasReais[0]?.id })}
						disabled={carregando || !colunasReais.length}
					>
						<Plus className="h-4 w-4" />
						Novo cartão
					</Button>
				</div>
			</div>

			{carregando ? (
				<div className="flex flex-1 items-center justify-center">
					<Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
				</div>
			) : colunas.length === 0 ? (
				<EmptyStateCard
					variant="flex"
					size="lg"
					intent="primary"
					icon={KanbanSquare}
					className="px-8 py-16"
					title="Seu funil ainda não tem etapas."
					subtitle="Crie a primeira etapa para começar a organizar leads e clientes em um fluxo visual. Arraste para mover, e o chat acompanha automaticamente."
					action={{
						label: colunasReais.length ? "Nova etapa" : "Criar a primeira etapa",
						icon: Plus,
						onClick: () => setColunaEmEdicao({}),
					}}
				/>
			) : (
				<div className="flex min-h-0 flex-1 items-stretch gap-4 overflow-x-auto px-6 py-5">
					{colunas.map((coluna) => {
						const cartoes = coluna.cards || [];
						const alvoAqui = alvo === coluna.id;

						return (
							<section
								key={coluna.id}
								onDragOver={(e) => {
									e.preventDefault();
									setAlvo(coluna.id);
								}}
								onDragLeave={() =>
									setAlvo((a) => (a === coluna.id ? null : a))
								}
								onDrop={(e) => {
									e.preventDefault();
									setAlvo(null);
									const cartao = arrastando.current;
									arrastando.current = null;
									if (cartao) moverCartao(cartao, coluna.id);
								}}
								className={cn(
									"group/col flex w-[19.5rem] shrink-0 flex-col overflow-hidden rounded-2xl bg-muted/40 ring-1 ring-border/60 transition-colors",
									coluna.isSystem && "bg-muted/20",
									alvoAqui && "bg-primary/5 ring-2 ring-primary/50",
								)}
							>
								<header className="flex items-center gap-2.5 px-3.5 pb-2.5 pt-3">
									{/* A cor da etapa vira estrutura, não enfeite: é a barra que
									    marca a coluna do começo ao fim da leitura. */}
									<span
										aria-hidden
										className={cn(
											"h-4 w-1 shrink-0 rounded-full",
											coluna.isSystem && "bg-muted-foreground/50",
										)}
										style={
											coluna.isSystem
												? undefined
												: { backgroundColor: coluna.color }
										}
									/>
									<h2 className="min-w-0 flex-1 truncate text-[0.7rem] font-semibold uppercase tracking-[0.09em] text-foreground/80">
										{coluna.name}
									</h2>
									<span className="rounded-full bg-background/70 px-1.5 py-0.5 text-[0.7rem] font-medium tabular-nums text-muted-foreground">
										{cartoes.length}
									</span>

									{coluna.triggerMessage && (
										<span
											title={`Ao chegar aqui, o cliente recebe: "${coluna.triggerMessage}"`}
											className="text-primary"
										>
											<Megaphone className="h-3.5 w-3.5" />
										</span>
									)}

									{coluna.isSystem ? (
										<span
											title="Etapa do sistema: mostra sozinha os atendimentos em aberto e não pode ser editada nem excluída."
											className="text-muted-foreground/70"
										>
											<Lock className="h-3.5 w-3.5" />
										</span>
									) : (
										<DropdownMenu>
											<DropdownMenuTrigger asChild>
												<Button
													variant="ghost"
													size="icon"
													className="h-6 w-6 opacity-0 transition-opacity focus-visible:opacity-100 group-hover/col:opacity-100 data-[state=open]:opacity-100"
												>
													<MoreVertical className="h-3.5 w-3.5" />
													<span className="sr-only">Ações da etapa</span>
												</Button>
											</DropdownMenuTrigger>
											<DropdownMenuContent align="end">
												<DropdownMenuItem
													onClick={() =>
														setCartaoEmEdicao({ columnId: coluna.id })
													}
												>
													<Plus className="h-4 w-4" />
													Novo cartão aqui
												</DropdownMenuItem>
												<DropdownMenuItem
													onClick={() => setColunaEmEdicao(coluna)}
												>
													<Pencil className="h-4 w-4" />
													Editar etapa
												</DropdownMenuItem>
												<DropdownMenuSeparator />
												<DropdownMenuItem
													className="text-destructive"
													onClick={() => excluirColuna(coluna)}
												>
													<Trash2 className="h-4 w-4" />
													Excluir etapa
												</DropdownMenuItem>
											</DropdownMenuContent>
										</DropdownMenu>
									)}
								</header>

								<div className="flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto px-2.5 pb-3">
									{!cartoes.length && (
										<p
											className={cn(
												"mx-0.5 rounded-xl px-3 py-8 text-center text-xs leading-relaxed text-muted-foreground",
												!coluna.isSystem && "border border-dashed",
											)}
										>
											{coluna.isSystem
												? "Nenhum atendimento em aberto agora."
												: "Solte um contato aqui para começar."}
										</p>
									)}

									{cartoes.map((cartao) => (
										<article
											key={cartao.id}
											draggable
											onDragStart={() => {
												arrastando.current = cartao;
											}}
											onDragEnd={() => {
												arrastando.current = null;
												setAlvo(null);
											}}
											className="group/card relative cursor-grab select-none rounded-xl bg-card p-3 ring-1 ring-border/70 transition-all hover:-translate-y-px hover:shadow-md hover:ring-border active:cursor-grabbing"
										>
											<div className="flex items-start gap-2.5">
												<AvatarPreview
													className="h-8 w-8"
													previewUrl={cartao.contact?.profilePicUrl}
													alt=""
												>
													<AvatarImage
														src={cartao.contact?.profilePicUrl}
														alt=""
													/>
													<AvatarFallback className="text-[0.65rem] font-medium">
														{iniciais(cartao.title)}
													</AvatarFallback>
												</AvatarPreview>

												<button
													type="button"
													onClick={() =>
														cartao.isTicket
															? navigate(`/tickets/${cartao.ticketId}`)
															: setCartaoEmEdicao(cartao)
													}
													className="min-w-0 flex-1 text-left"
												>
													<p className="truncate text-sm font-medium leading-tight">
														{cartao.title}
													</p>
													{/* A segunda linha só existe se disser algo novo: repetir o
													    nome do cliente embaixo do nome do cliente é ruído. */}
													{(cartao.isTicket
														? cartao.lastMessage || cartao.contact?.number
														: cartao.contact?.name === cartao.title
															? cartao.contact?.number
															: cartao.contact?.name ||
																cartao.contact?.number) && (
														<p className="mt-0.5 truncate text-xs text-muted-foreground">
															{cartao.isTicket
																? cartao.lastMessage || cartao.contact?.number
																: cartao.contact?.name === cartao.title
																	? cartao.contact?.number
																	: cartao.contact?.name ||
																		cartao.contact?.number}
														</p>
													)}
												</button>

												{cartao.user && (
													<span
														title={`Atende: ${cartao.user.name}`}
														className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary/10 text-[0.6rem] font-semibold text-primary"
													>
														{iniciais(cartao.user.name)}
													</span>
												)}

												<DropdownMenu>
													<DropdownMenuTrigger asChild>
														<Button
															variant="ghost"
															size="icon"
															className="-mr-1 h-6 w-6 shrink-0 opacity-0 transition-opacity focus-visible:opacity-100 group-hover/card:opacity-100 data-[state=open]:opacity-100"
														>
															<MoreVertical className="h-3.5 w-3.5" />
															<span className="sr-only">Ações do cartão</span>
														</Button>
													</DropdownMenuTrigger>
													<DropdownMenuContent align="end">
														{cartao.isTicket ? (
															<DropdownMenuItem
																onClick={() =>
																	navigate(`/tickets/${cartao.ticketId}`)
																}
															>
																<MessageSquare className="h-4 w-4" />
																Abrir conversa
															</DropdownMenuItem>
														) : (
															<>
																{cartao.contactId && (
																	<DropdownMenuItem
																		onClick={() => abrirConversa(cartao)}
																	>
																		<MessageSquare className="h-4 w-4" />
																		Abrir conversa
																	</DropdownMenuItem>
																)}
																<DropdownMenuItem
																	onClick={() => setCartaoEmEdicao(cartao)}
																>
																	<Pencil className="h-4 w-4" />
																	Editar
																</DropdownMenuItem>
															</>
														)}
														{colunasReais.length > 0 && (
															<DropdownMenuSub>
																<DropdownMenuSubTrigger>
																	{cartao.isTicket
																		? "Puxar para"
																		: "Mover para"}
																</DropdownMenuSubTrigger>
																<DropdownMenuPortal>
																	<DropdownMenuSubContent>
																		{colunasReais
																			.filter((c) => c.id !== cartao.columnId)
																			.map((destino) => (
																				<DropdownMenuItem
																					key={destino.id}
																					onClick={() =>
																						moverCartao(cartao, destino.id)
																					}
																				>
																					<span
																						aria-hidden
																						className="h-2 w-2 rounded-full"
																						style={{
																							backgroundColor: destino.color,
																						}}
																					/>
																					{destino.name}
																				</DropdownMenuItem>
																			))}
																	</DropdownMenuSubContent>
																</DropdownMenuPortal>
															</DropdownMenuSub>
														)}
														{!cartao.isTicket && (
															<>
																<DropdownMenuSeparator />
																<DropdownMenuItem
																	className="text-destructive"
																	onClick={() => excluirCartao(cartao)}
																>
																	<Trash2 className="h-4 w-4" />
																	Excluir
																</DropdownMenuItem>
															</>
														)}
													</DropdownMenuContent>
												</DropdownMenu>
											</div>

											{(cartao.isTicket
												? cartao.queue ||
													cartao.status === "pending" ||
													cartao.unreadMessages > 0
												: formatarMoeda(cartao.value) ||
													cartao.dueDate) && (
												<div className="mt-2.5 flex flex-wrap items-center gap-1.5 pl-[2.625rem]">
													{cartao.isTicket && cartao.queue && (
														<span
															className="inline-flex items-center rounded-full px-1.5 py-0.5 text-[0.7rem] font-medium"
															style={{
																color: cartao.queue.color,
																backgroundColor: `${cartao.queue.color}1a`,
															}}
														>
															{cartao.queue.name}
														</span>
													)}
													{cartao.isTicket && cartao.status === "pending" && (
														<Badge
															variant="outline"
															className="h-5 px-1.5 text-[0.7rem] font-normal"
														>
															Aguardando
														</Badge>
													)}
													{cartao.isTicket && cartao.unreadMessages > 0 && (
														<Badge className="h-5 px-1.5 text-[0.7rem] tabular-nums">
															{cartao.unreadMessages}
														</Badge>
													)}
													{formatarMoeda(cartao.value) && (
														<Badge
															variant="secondary"
															className="h-5 px-1.5 text-[0.7rem] tabular-nums"
														>
															{formatarMoeda(cartao.value)}
														</Badge>
													)}
													{cartao.dueDate && (
														<Badge
															variant="outline"
															className="h-5 gap-1 px-1.5 text-[0.7rem] font-normal tabular-nums"
														>
															<CalendarDays className="h-3 w-3" />
															{formatarData(cartao.dueDate)}
														</Badge>
													)}
												</div>
											)}
										</article>
									))}
								</div>
							</section>
						);
					})}

					<button
						type="button"
						onClick={() => setColunaEmEdicao({})}
						className="flex h-fit w-[13rem] shrink-0 items-center justify-center gap-2 rounded-2xl border border-dashed px-3 py-4 text-sm text-muted-foreground transition-colors hover:border-primary/40 hover:bg-primary/5 hover:text-foreground"
					>
						<Plus className="h-4 w-4" />
						{colunasReais.length ? "Nova etapa" : "Criar a primeira etapa"}
					</button>
				</div>
			)}

			<ColunaModal
				open={!!colunaEmEdicao}
				coluna={colunaEmEdicao}
				onClose={() => setColunaEmEdicao(null)}
				onSaved={carregar}
			/>

			<CartaoModal
				open={!!cartaoEmEdicao}
				cartao={cartaoEmEdicao}
				colunas={colunasReais}
				onClose={() => setCartaoEmEdicao(null)}
				onSaved={carregar}
			/>
		</MainContainer>
	);
};

export default Kanban;
